import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useAuth } from "@/shared/hooks/useAuth";

interface ProfilePermissions {
  qualificacao: string;
  permissoes: Record<string, Record<string, boolean>>;
  nome: string;
  user_id: string;
}

/** Cache global — evita SELECTs duplicados em profiles (mesmo dado que usePermissions pede). */
let _finCacheProfile: { userId: string; data: ProfilePermissions | null; ts: number } | null = null;
const FIN_CACHE_TTL = 120_000;

export const useFinanceAccess = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfilePermissions | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    if (_finCacheProfile && _finCacheProfile.userId === user.id && Date.now() - _finCacheProfile.ts < FIN_CACHE_TTL) {
      setProfile(_finCacheProfile.data);
      setLoading(false);
      return;
    }

    const fetchAccess = async () => {
      try {
        const { data } = await supabase
          .from("profiles")
          .select("qualificacao, permissoes, nome, user_id")
          .eq("user_id", user.id)
          .maybeSingle();
        const parsed = data
          ? {
              qualificacao: data.qualificacao,
              permissoes: (data.permissoes as Record<string, Record<string, boolean>>) || {},
              nome: data.nome,
              user_id: data.user_id,
            }
          : null;
        _finCacheProfile = { userId: user.id, data: parsed, ts: Date.now() };
        setProfile(parsed);
      } catch (err) {
        console.error("[useFinanceAccess] fetch failed:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAccess();
  }, [user?.id]);

  const isSocioGestor = profile?.qualificacao === "socio_gestor";
  const isSocio = profile?.qualificacao === "socio" || isSocioGestor;
  const isAssociado = profile?.qualificacao === "associado";
  const hasFinancePermission = isSocio || !!profile?.permissoes?.financeiro?.visualizar;
  const canAccessFullFinance = hasFinancePermission;

  return {
    profile,
    loading,
    isSocioGestor,
    isSocio,
    isAssociado,
    hasFinancePermission,
    canAccessFullFinance,
  };
};
